import { useEffect, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  Modal,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import { Button } from '../../components/Button';
import { useTranslation } from '../../hooks/useTranslation';
import api from '../../services/api';
import { HOST_API } from '../../config/hosts';

const inputSx = {
  backgroundColor: '#fff',
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#E0E0E0' },
    '&:hover fieldset': { borderColor: '#83D84D' },
    '&.Mui-focused fieldset': { borderColor: '#83D84D' },
  },
};

const ConfigureValuesModal = ({ open, onClose, onSaved }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [avulsoPrice, setAvulsoPrice] = useState('');
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    if (open) fetchConfig();
  }, [open]);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${HOST_API}/v1/plan-price-config`);
      setAvulsoPrice(String(data.consultation_price));
      setPlans(
        data.plans.map((p) => ({
          id: p.id,
          amount: String(p.amount),
          price: String(p.price),
          unit_price: String(p.unit_price),
        }))
      );
    } catch {
      toast.error(t('redscoreSuperAdmin.configModal.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  const handlePlanChange = (index, field, value) => {
    setPlans((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };

      const amt = parseFloat(updated[index].amount);
      const prc = parseFloat(updated[index].price);
      if (amt > 0 && prc > 0) {
        updated[index].unit_price = (prc / amt).toFixed(2);
      } else {
        updated[index].unit_price = '';
      }

      return updated;
    });
  };

  const addPlan = () => {
    setPlans((prev) => [...prev, { amount: '', price: '', unit_price: '' }]);
  };

  const removePlan = (index) => {
    setPlans((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        consultation_price: parseFloat(avulsoPrice),
        plans: plans.map((p) => ({
          ...(p.id ? { id: p.id } : {}),
          amount: parseInt(p.amount, 10),
          price: parseFloat(p.price),
          unit_price: parseFloat(p.unit_price),
        })),
      };
      await api.put(`${HOST_API}/v1/plan-price-config`, body);
      toast.success(t('redscoreSuperAdmin.configModal.savedSuccess'));
      onSaved?.();
      onClose();
    } catch {
      toast.error(t('redscoreSuperAdmin.configModal.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 2 }}
    >
      <Box
        sx={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          width: '100%',
          maxWidth: 680,
          maxHeight: '90vh',
          overflowY: 'auto',
          outline: 'none',
          boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          '&::-webkit-scrollbar': { width: '8px' },
          '&::-webkit-scrollbar-track': { background: '#F5F5F5', borderRadius: '0 12px 12px 0' },
          '&::-webkit-scrollbar-thumb': { background: '#C0C0C0', borderRadius: '8px' },
          '&::-webkit-scrollbar-thumb:hover': { background: '#A0A0A0' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            p: '24px 24px 0 24px',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '20px', fontWeight: 700, color: '#1D2530' }}>
              {t('redscoreSuperAdmin.configModal.title')}
            </Typography>
            <Typography sx={{ fontSize: '13px', color: '#9E9E9E', mt: '4px' }}>
              {t('redscoreSuperAdmin.configModal.subtitle')}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: '#9E9E9E', mt: '-4px' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Box sx={{ p: '24px' }}>
          {loading ? (
            <Typography sx={{ color: '#9E9E9E', textAlign: 'center', py: 4 }}>
              Carregando...
            </Typography>
          ) : (
            <>
              <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1D2530', mb: '8px' }}>
                {t('redscoreSuperAdmin.configModal.avulsoLabel')}
              </Typography>
              <TextField
                value={avulsoPrice}
                onChange={(e) => setAvulsoPrice(e.target.value)}
                type="number"
                inputProps={{ min: 0, step: '0.01' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: '#9E9E9E', fontSize: '14px' }}>R$</Typography>
                    </InputAdornment>
                  ),
                }}
                sx={{ ...inputSx, mb: '6px', width: '220px' }}
              />
              <Typography sx={{ fontSize: '12px', color: '#9E9E9E', mb: '28px' }}>
                {t('redscoreSuperAdmin.configModal.avulsoHelper')}
              </Typography>

              <Typography sx={{ fontSize: '16px', fontWeight: 600, color: '#1D2530', mb: '4px' }}>
                {t('redscoreSuperAdmin.configModal.packagesTitle')}
              </Typography>
              <Typography sx={{ fontSize: '13px', color: '#9E9E9E', mb: '16px' }}>
                {t('redscoreSuperAdmin.configModal.packagesSubtitle')}
              </Typography>

              <Box
                sx={{
                  maxHeight: '280px',
                  overflowY: 'auto',
                  overflowX: 'auto',
                  mb: '16px',
                }}
              >
                {plans.map((plan, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: 'flex',
                      gap: '12px',
                      alignItems: 'center',
                      mb: '16px',
                      minWidth: '500px',
                      maxWidth: '700px',
                      justifyContent: 'center',
                    }}
                  >
                    <Box sx={{}}>
                      <Typography sx={{ fontSize: '12px', color: '#737373', mb: '6px' }}>
                        {t('redscoreSuperAdmin.configModal.colAmount')}
                      </Typography>
                      <TextField
                        value={plan.amount}
                        onChange={(e) => handlePlanChange(index, 'amount', e.target.value)}
                        type="number"
                        inputProps={{ min: 1 }}
                        fullWidth
                        sx={inputSx}
                      />
                    </Box>
                    <Box sx={{}}>
                      <Typography sx={{ fontSize: '12px', color: '#737373', mb: '6px' }}>
                        {t('redscoreSuperAdmin.configModal.colTotal')}
                      </Typography>
                      <TextField
                        value={plan.price}
                        onChange={(e) => handlePlanChange(index, 'price', e.target.value)}
                        type="number"
                        inputProps={{ min: 0, step: '0.01' }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography sx={{ color: '#9E9E9E', fontSize: '14px' }}>R$</Typography>
                            </InputAdornment>
                          ),
                        }}
                        fullWidth
                        sx={inputSx}
                      />
                    </Box>
                    <Box sx={{}}>
                      <Typography sx={{ fontSize: '12px', color: '#737373', mb: '6px' }}>
                        {t('redscoreSuperAdmin.configModal.colUnit')}
                      </Typography>
                      <TextField
                        value={plan.unit_price}
                        disabled
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Typography sx={{ color: '#9E9E9E', fontSize: '14px' }}>R$</Typography>
                            </InputAdornment>
                          ),
                        }}
                        fullWidth
                        sx={{ ...inputSx, backgroundColor: '#F5F5F5' }}
                      />
                    </Box>
                    <IconButton
                      onClick={() => removePlan(index)}
                      sx={{ color: '#E53935', px: 0, marginTop: '24px', marginRight: '12px', flexShrink: 0 }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Box>
                ))}
              </Box>

              <Box
                onClick={addPlan}
                sx={{
                  border: '2px dashed #E0E0E0',
                  borderRadius: '8px',
                  py: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  cursor: 'pointer',
                  color: '#737373',
                  mb: '16px',
                  '&:hover': { borderColor: '#83D84D', color: '#83D84D' },
                }}
              >
                <AddIcon fontSize="small" />
                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                  {t('redscoreSuperAdmin.configModal.addPackage')}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <Button
                  fullWidth={false}
                  onClick={onClose}
                  sx={{
                    border: '1px solid #E0E0E0',
                    color: '#1D2530',
                    backgroundColor: '#fff',
                    '&:hover': { backgroundColor: '#F5F5F5' },
                    px: '24px',
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  fullWidth={false}
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    backgroundColor: '#83D84D',
                    '&:hover': { backgroundColor: '#6bb03a' },
                    color: '#1D2530',
                    fontWeight: 700,
                    px: '24px',
                  }}
                >
                  {saving ? 'Salvando...' : t('redscoreSuperAdmin.configModal.saveButton')}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ConfigureValuesModal;
